import type { AxiosError } from "axios";
import { MissionSchema } from "../schemas/mission.schema";
import {
  Mission,
  MissionReward,
  MissionRewardType,
} from "../types/mission.type";

export const generateMissionFieldId = () => {
  const randomGuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return `mission-field-${randomGuid}`;
};

export const missionDefaultValues: MissionSchema = {
  mission_type: "tcard",
  establishment: null,
  status: "active",
  name: "",
  description: "",
  allow_multiple_times: false,
  max_times: null,
  minimum_loyalty: "indifferent",
  start_date: "",
  end_date: "",
  conditions: [],
  rewards: [],
};

export type RewardFormValue = MissionSchema["rewards"][number] & { id: string };

export const rewardTypeLabels: Record<MissionRewardType, string> = {
  product: "Produto",
  points: "Pontos",
  tickets: "Bilhetes",
};

export const missionBackendErrorFieldMap: Record<
  string,
  keyof MissionSchema | "_base"
> = {
  display_name: "name",
  available_from: "start_date",
  available_until: "end_date",
  maximum_completions_per_user: "max_times",
  establishment_company_branch_id: "establishment",
  requirements: "conditions",
  requirements_attributes: "conditions",
  rewards: "rewards",
  rewards_attributes: "rewards",
  base: "_base",
};

export const transformMissionBackendErrors = (
  errors: Record<string, string[]>,
) => {
  return Object.entries(errors).map(([key, value]) => {
    const normalizedKey = missionBackendErrorFieldMap[key] ?? key;
    if (normalizedKey === "_base") {
      const payload = Array.isArray(value) ? value : [String(value)];
      return { _base: payload };
    }

    const message = Array.isArray(value) ? value.join(", ") : String(value);
    return { [normalizedKey]: message };
  });
};

type MissionErrorResponse = {
  errors?: Record<string, string[]>;
};

export const extractMissionBackendErrorMessages = (
  error: AxiosError,
): string[] => {
  const data = error.response?.data as MissionErrorResponse | undefined;
  if (!data?.errors) return [];

  return Object.values(data.errors)
    .flatMap((messages) => (Array.isArray(messages) ? messages : []))
    .filter((message) => typeof message === "string" && message.length > 0);
};

export const mapMissionRewardType = (
  value?: MissionReward["reward_type"] | string,
): MissionRewardType => {
  if (value === "points") return "points";
  if (value === "product" || value === "Tmais::Product") return "product";
  if (value === "tickets" || value === "Tmais::Raffle" || value === "Tmais::RewardTicket") return "tickets";
  return "product";
};

export const resolveIncomingRewardType = (
  reward?: MissionReward,
): MissionRewardType => {
  const rawType = reward?.reward_type as string | undefined;
  if (rawType === "points") return "points";
  if (rawType === "product" || rawType === "Tmais::Product") return "product";
  if (rawType === "tickets" || rawType === "Tmais::Raffle" || rawType === "Tmais::RewardTicket") return "tickets";
  if (!reward?.product_id && !reward?.reward_id) return "points";
  return mapMissionRewardType(rawType);
};

export const mapRewardToFormValue = (
  reward?: MissionReward,
): RewardFormValue => {
  const resolvedType = resolveIncomingRewardType(reward);
  const resolvedProductId =
    reward?.product_id ?? reward?.reward_id ?? undefined;
  const normalizedProductId =
    (resolvedType === "product" || resolvedType === "tickets") && resolvedProductId
      ? String(resolvedProductId)
      : undefined;
  const fallbackProductLabel =
    reward?.product_name || reward?.reward_label || undefined;
  const normalizedRewardLabel =
    (resolvedType === "product" || resolvedType === "tickets")
      ? fallbackProductLabel
      : reward?.reward_label || rewardTypeLabels.points;

  return {
    id: reward?.id || generateMissionFieldId(),
    reward_type: resolvedType,
    quantity: Number(reward?.quantity ?? 1),
    reward_id: normalizedProductId,
    product_id: normalizedProductId,
    reward_label: normalizedRewardLabel,
    product_name:
      (resolvedType === "product" || resolvedType === "tickets") ? fallbackProductLabel || undefined : undefined,
  };
};

export const buildMissionDefaultValues = (mission?: Mission): MissionSchema => {
  if (!mission) {
    return { ...missionDefaultValues };
  }

  return {
    ...missionDefaultValues,
    ...mission,
    establishment: mission.establishment ?? null,
    conditions: mission.conditions ?? [],
    rewards: (mission.rewards ?? []).map((reward) =>
      mapRewardToFormValue(reward),
    ),
    max_times: mission.max_times ?? null,
  };
};

export type MissionFormSubmitHelpers = {
  onError: (error: AxiosError) => void;
};